import React, { useContext, useEffect, useRef, useState } from 'react';
import {
    Modal, Box, SpaceBetween, Button, FormField, Input, Textarea, Select, Alert
} from '@amzn/awsui-components-react';
import {
    MultidimensionalAllocationContext
} from 'src/services/multidimensional-allocation/MultidimensionalAllocationContext';
import TPENavigator from 'src/components/TPENavigator';
import CONSTANTS from 'src/utils/constants';
import { CreateDerivedAllocationSheetRequest } from 'src/models/derived-allocation-sheet/CreateDerivedAllocationSheetRequest';
import { validateMdaSheetName } from '../validation/mdaSheetValidation';

interface Props {
    visible: boolean;
    onHide: () => void;
}

const CLONE_FROM_EMPTY_OPTION = { label: 'None (empty sheet)', value: '' };

export default function CreateDerivedAllocationSheetModal({ visible, onHide }: Props) {
    const { services } = useContext(MultidimensionalAllocationContext);
    const service = services.derivedAllocationSheetService;

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [sourceSheetId, setSourceSheetId] = useState('');
    const [isSubmitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | undefined>(undefined);
    const [nameError, setNameError] = useState<string | undefined>(undefined);
    const [navigateURL, setNavigateURL] = useState('');
    const cancelledRef = useRef(false);

    // Fetch the clone-picker options only when the modal is open, bumping a counter on each
    // false->true transition so a sheet created earlier in the same session shows up without a
    // reload. The query is derived solely from the token (0 means never opened, so no fetch),
    // which keeps it changing exactly once per open and avoids a double fetch.
    const [reloadToken, setReloadToken] = useState(0);
    useEffect(() => {
        if (visible) {
            setReloadToken(t => t + 1);
            setSourceSheetId('');
            cancelledRef.current = false;
        }
    }, [visible]);
    const cloneQuery = reloadToken > 0 ? `fetch-for-clone-picker-${reloadToken}` : '';
    const [existing, existingLoading, existingError] = service.listDerivedAllocationSheets(cloneQuery, 'ACTIVE');
    // A sheet is cloneable only when it has a live v0 row (currentVersion >= 0). The backend
    // listDefinitions prefers the v-1 draft row when one exists, so a live sheet currently being
    // revised comes back with its draft status; filtering on currentVersion rather than status
    // keeps those entries in the picker.
    const cloneOptions = [
        CLONE_FROM_EMPTY_OPTION,
        ...(existing?.sheets ?? [])
            .filter(s => s.currentVersion !== undefined && s.currentVersion >= 0)
            .map(s => ({ label: s.sheetName, value: s.sheetId })),
    ];

    const resetForm = () => {
        setName('');
        setDescription('');
        setSourceSheetId('');
        setNameError(undefined);
        setSubmitError(undefined);
    };

    const handleCancel = () => {
        cancelledRef.current = true;
        resetForm();
        onHide();
    };

    const handleSubmit = async () => {
        setSubmitError(undefined);
        setNameError(undefined);

        const nameValidationError = validateMdaSheetName(name);
        if (nameValidationError) {
            setNameError(nameValidationError);
            return;
        }

        try {
            setSubmitting(true);
            const payload: CreateDerivedAllocationSheetRequest = {
                sheetName: name.trim(),
                description: description.trim(),
            };
            if (sourceSheetId) {
                payload.sourceSheetId = sourceSheetId;
                payload.sourceVersion = 0;
            }
            const response = await service.createDerivedAllocationSheet(payload);
            if (cancelledRef.current) {
                return;
            }
            const sheetId = response.sheetData?.sheetId;
            if (!sheetId) {
                setSubmitError('Create succeeded but no sheet id was returned');
                return;
            }
            setNavigateURL(`${CONSTANTS.PAGE_NAV.MULTIDIMENSIONAL_ALLOCATION_DERIVED_SHEET_DETAILS.URL}/${sheetId}`);
            resetForm();
            onHide();
        } catch (err) {
            if (cancelledRef.current) {
                return;
            }
            const msg = err instanceof Error ? err.message : String(err);
            setSubmitError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (<>
        <TPENavigator path={navigateURL} />
        <Modal
            onDismiss={(event) => { event.detail.reason !== 'overlay' && handleCancel(); }}
            visible={visible}
            closeAriaLabel="Close modal"
            size="medium"
            footer={<Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                    <Button variant="link" onClick={handleCancel}>Cancel</Button>
                    <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting} loading={isSubmitting}>
                        Create
                    </Button>
                </SpaceBetween>
            </Box>}
            header="Create derived allocation sheet"
        >
            <SpaceBetween direction="vertical" size="m">
                <Box>
                    A derived allocation sheet computes recipient percentages from a formula-driven
                    four-step configuration. You can start empty, or clone the structure of an existing
                    derived sheet.
                </Box>

                <FormField label="Name" errorText={nameError}>
                    <Input
                        value={name}
                        onChange={({ detail }) => setName(detail.value)}
                        placeholder="Enter name without special characters"
                    />
                </FormField>

                <FormField label="Description">
                    <Textarea
                        value={description}
                        onChange={({ detail }) => setDescription(detail.value)}
                        placeholder="Enter description"
                        rows={3}
                    />
                </FormField>

                <FormField
                    label="Clone from"
                    description="Optional. Copies all Step 1 to 4 rows from the selected sheet."
                >
                    <Select
                        selectedOption={cloneOptions.find(o => o.value === sourceSheetId) ?? CLONE_FROM_EMPTY_OPTION}
                        options={cloneOptions}
                        onChange={({ detail }) => setSourceSheetId(detail.selectedOption?.value ?? '')}
                        placeholder="None (empty sheet)"
                        disabled={existingLoading}
                        statusType={existingLoading ? 'loading' : (existingError ? 'error' : 'finished')}
                        loadingText="Loading derived sheets..."
                        errorText="Failed to load derived sheets"
                    />
                </FormField>

                {!!submitError && (
                    <Alert
                        type="error"
                        dismissible
                        onDismiss={() => setSubmitError(undefined)}
                        header="Create failed"
                    >
                        {submitError}
                    </Alert>
                )}
            </SpaceBetween>
        </Modal>
    </>);
}
